import { useEffect, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';
import { Skeleton } from 'shared/components/Skeleton';
import { useDashboardData } from 'shared/contexts/DashboardDataContext';
import { useClickOutside } from 'shared/hooks/useClickOutside';
import { useMutation } from 'shared/hooks/useMutation';
import { useSitemap } from 'shared/hooks/useSitemap';
import { BellIcon } from 'shared/icons/BellIcon';
import { cn } from 'shared/utils/cn';

export const NotificationDropdown = () => {
  const { t } = useTranslation();
  const { home } = useDashboardData();
  const sitemap = useSitemap();
  const location = useLocation();
  const [markNotificationAsRead] = useMutation();
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const panelId = useId();
  const titleId = useId();
  const notificationCount = home?.notifications.length;

  useClickOutside(wrapperRef, () => setOpen(false));

  useEffect(() => setOpen(false), [location.key]);

  useEffect(() => {
    if (!open) return undefined;
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      setOpen(false);
      buttonRef.current?.focus();
    };
    document.addEventListener('keydown', closeOnEscape);
    return () => document.removeEventListener('keydown', closeOnEscape);
  }, [open]);

  return (
    <div
      ref={wrapperRef}
      className="relative"
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget)) setOpen(false);
      }}
    >
      <button
        ref={buttonRef}
        type="button"
        aria-expanded={open}
        aria-controls={panelId}
        aria-label={
          notificationCount === undefined
            ? t('home:notifications.title')
            : t('home:notifications.open', { count: notificationCount })
        }
        onClick={() => setOpen((value) => !value)}
        className={cn(
          'hover:bg-primary/10 hover:text-primary relative flex h-9 w-9 items-center justify-center rounded-full text-black/70 transition-colors',
          open && 'bg-primary/10 text-primary',
        )}
      >
        <BellIcon aria-hidden="true" className="h-5 w-5" />
        {!!notificationCount && (
          <span
            aria-hidden="true"
            className="bg-primary absolute -top-0.5 -right-1 flex h-[18px] min-w-[18px] items-center justify-center rounded-full px-1 text-[10px] font-bold text-white tabular-nums"
          >
            {notificationCount > 99 ? '99+' : notificationCount}
          </span>
        )}
      </button>
      {open && (
        <section
          id={panelId}
          aria-labelledby={titleId}
          className="shadow-paper fixed inset-x-4 top-[54px] z-40 overflow-hidden rounded-2xl border border-black/10 bg-white md:absolute md:top-full md:right-0 md:left-auto md:mt-3 md:w-[400px]"
        >
          <div className="flex items-center justify-between gap-4 border-b border-black/10 px-5 py-4">
            <h2 id={titleId} className="text-base font-bold tracking-tight">
              {t('home:notifications.title')}
            </h2>
            {notificationCount !== undefined && (
              <span className="bg-primary/10 text-primary rounded-full px-2.5 py-1 text-xs font-bold tabular-nums">
                {notificationCount}
              </span>
            )}
          </div>
          <div className="max-h-[min(420px,calc(100dvh-180px))] overflow-y-auto overscroll-contain">
            {!home && (
              <div role="status" aria-label={t('common:loading')} className="space-y-5 p-5">
                <span className="sr-only">{t('common:loading')}</span>
                {[0, 1, 2].map((item) => (
                  <div key={item} className="space-y-2">
                    <Skeleton className="h-3 w-20" />
                    <Skeleton className="h-4 w-full" />
                    <Skeleton className="h-4 w-2/3" />
                  </div>
                ))}
              </div>
            )}
            {home?.notifications.length === 0 && (
              <div className="px-6 py-10 text-center">
                <BellIcon aria-hidden="true" className="text-primary mx-auto mb-3 h-7 w-7" />
                <p className="text-sm leading-relaxed text-black/60">
                  {t('home:notifications.placeholder')}
                </p>
              </div>
            )}
            {!!home?.notifications.length && (
              <ul className="divide-y divide-black/5">
                {home.notifications.map((notification) => {
                  const content = (
                    <>
                      <span className="mb-1.5 block text-xs text-black/50">
                        {notification.displayDate}
                      </span>
                      <span className="block text-sm leading-relaxed text-black/80">
                        {t(`notification:${notification.type || 'typeUnknown'}`, {
                          jobOrderLabel: notification.jobOrderLabel,
                          blindLabel: notification.blindLabel,
                          meetingDate: notification.meetingDate,
                        })}
                      </span>
                      {notification.jobOrderId && (
                        <span className="text-primary mt-2 block text-xs font-bold">
                          {t('action:seeDetails')} <span aria-hidden="true">↗</span>
                        </span>
                      )}
                    </>
                  );
                  return (
                    <li key={notification.id}>
                      {notification.jobOrderId ? (
                        <Link
                          to={sitemap.jobOrder(notification.jobOrderId)}
                          onClick={() => {
                            setOpen(false);
                            markNotificationAsRead(`notification/${notification.id}/click`);
                          }}
                          className="hover:bg-primary/5 focus-visible:bg-primary/5 block px-5 py-4 transition-colors focus-visible:outline-offset-[-3px]"
                        >
                          {content}
                        </Link>
                      ) : (
                        <div className="px-5 py-4">{content}</div>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
          <Link
            to={`${sitemap.dashboard}#notifications`}
            onClick={() => setOpen(false)}
            className="text-primary hover:bg-primary/5 block border-t border-black/10 px-5 py-3.5 text-center text-sm font-bold transition-colors focus-visible:outline-offset-[-3px]"
          >
            {t('home:notifications.viewOnDashboard')}
          </Link>
        </section>
      )}
    </div>
  );
};
